import { useEffect } from 'react';
import {
  ArrowRight,
  Handshake,
  Loader2,
  ShieldAlert,
  Trash2,
  Undo2,
} from 'lucide-react';

interface ReferralRemoveDialogProps {
  open: boolean;
  /** Partner currently linked to the admission (kept visible for confirmation). */
  referralName?: string;
  /** Commission amount for the stay, already formatted (e.g. "৳1,500.00"). */
  commissionLabel?: string;
  loading?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  /**
   * Render above another dialog (the referral editor opens this from its
   * footer). Defaults to a layer above the standard `z-50` modals.
   */
  layerClassName?: string;
}

/**
 * Modern destructive confirmation for unlinking a referral partner from an
 * admission. Shows exactly what is being removed (partner + flat commission)
 * before the caller fires the update that also clears the pending payout.
 */
export function ReferralRemoveDialog({
  open,
  referralName,
  commissionLabel,
  loading = false,
  onConfirm,
  onCancel,
  layerClassName = 'z-[60]',
}: ReferralRemoveDialogProps) {
  // Escape closes the dialog while it is not saving.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, loading, onCancel]);

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 ${layerClassName} flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm modal-overlay-enter`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="referral-remove-title"
      onClick={() => {
        if (!loading) onCancel();
      }}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl modal-panel-enter"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Accent strip keeps the destructive intent visible at a glance. */}
        <div className="h-1 w-full bg-gradient-to-r from-danger via-rose-500 to-warning" />

        <div className="px-5 pb-4 pt-5">
          <div className="flex items-start gap-3.5">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-danger/10 ring-4 ring-danger/5">
              <Trash2 size={20} className="text-danger" />
            </div>
            <div className="min-w-0 space-y-1">
              <h3
                id="referral-remove-title"
                className="text-base font-semibold tracking-tight text-text"
              >
                Remove referral?
              </h3>
              <p className="text-sm leading-relaxed text-text-muted">
                The referrer will no longer be linked to this admission and the
                commission is cleared.
              </p>
            </div>
          </div>

          {/* What exactly disappears */}
          <div className="mt-4 space-y-2 rounded-xl border border-border/70 bg-background/60 p-3">
            <div className="flex items-center justify-between gap-3">
              <span className="inline-flex items-center gap-1.5 text-xs text-text-muted">
                <Handshake size={13} />
                Referral partner
              </span>
              <span className="min-w-0 truncate text-sm font-semibold text-text">
                {referralName || 'Selected partner'}
              </span>
            </div>
            <div className="h-px bg-border/70" />
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-text-muted">Commission</span>
              <span className="flex items-center gap-2">
                <span className="text-sm font-semibold text-text line-through decoration-danger/70 decoration-2">
                  {commissionLabel || '৳0.00'}
                </span>
                <ArrowRight size={13} className="text-text-muted" />
                <span className="rounded-md bg-danger/10 px-2 py-0.5 text-xs font-bold text-danger">
                  ৳0
                </span>
              </span>
            </div>
          </div>

          <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-warning/10 px-2.5 py-2 text-[0.6875rem] leading-relaxed text-warning">
            <ShieldAlert size={13} className="mt-px shrink-0" />
            <span>
              Any unpaid payout for this stay is withdrawn from Billing →
              Payouts. A commission that has already been paid stays on record.
            </span>
          </p>
        </div>

        <div className="flex items-center gap-2 border-t border-border bg-background/50 px-4 py-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium text-text-muted transition-colors hover:bg-background hover:text-text disabled:opacity-50"
          >
            <Undo2 size={16} />
            Keep referral
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="ml-auto inline-flex items-center gap-2 rounded-xl bg-danger px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-danger/90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Trash2 size={16} />
            )}
            Remove referral
          </button>
        </div>
      </div>
    </div>
  );
}
